import { Badge, Paper, Stack, Switch, Table, Text } from '@mantine/core';
import { createSharedRegistry } from '@legacy-shared/widgets/core/registry';
import type { WidgetTier } from '@legacy-shared/widgets/core/types';
import { ErrorAlert } from '@/components/error-alert';
import { CONTACT_CENTER_FEATURE_ID } from './features-panel';
import { useWidgetToggle } from './use-widget-toggle';

const contactCenterWidgetIds = ['transferToQueue', 'transferToAgent'];

const alwaysOnTiers: WidgetTier[] = ['standard', 'system'];

const tierColors: Record<WidgetTier, string> = {
  system: 'gray',
  standard: 'green',
  premium: 'violet',
  custom: 'purple',
};

const catalogue = createSharedRegistry()
  .getAll()
  .map((w) => ({
    id: w.id,
    name: w.name,
    description: w.description,
    category: w.category,
    tier: w.tier,
  }));

type CatalogueWidget = (typeof catalogue)[number];

function WidgetRow({
  widget,
  checked,
  disabled,
  requiresContactCenter,
  onChange,
}: {
  widget: CatalogueWidget;
  checked: boolean;
  disabled: boolean;
  requiresContactCenter: boolean;
  onChange: (enabled: boolean) => void;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={2}>
          <Text fw={500}>{widget.name}</Text>
          <Text size="xs" c="dimmed">
            {widget.description}
          </Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" size="sm">
          {widget.category}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" size="sm" color={tierColors[widget.tier]}>
          {widget.tier}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.currentTarget.checked)} />
        {requiresContactCenter && (
          <Text size="xs" c="dimmed">
            Requires Contact Center
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

export function WidgetsPanel({
  organizationId,
  enabledWidgetIds,
}: {
  organizationId: string;
  enabledWidgetIds: Set<string>;
}) {
  const { error, pendingWidgetId, setEnabled } = useWidgetToggle(organizationId);
  const hasContactCenter = enabledWidgetIds.has(CONTACT_CENTER_FEATURE_ID);

  return (
    <Stack gap="md">
      <ErrorAlert message={error} />
      <Paper p={0} withBorder radius="md" style={{ overflow: 'hidden' }}>
        <Table layout="fixed">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Widget</Table.Th>
              <Table.Th w={120}>Category</Table.Th>
              <Table.Th w={100}>Tier</Table.Th>
              <Table.Th w={110}>Enabled</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {catalogue.map((widget) => {
              const alwaysOn = alwaysOnTiers.includes(widget.tier);
              const blockedByContactCenter = contactCenterWidgetIds.includes(widget.id) && !hasContactCenter;

              return (
                <WidgetRow
                  key={widget.id}
                  widget={widget}
                  checked={alwaysOn || enabledWidgetIds.has(widget.id)}
                  disabled={alwaysOn || blockedByContactCenter || pendingWidgetId === widget.id}
                  requiresContactCenter={blockedByContactCenter}
                  onChange={(enabled) => void setEnabled(widget.id, enabled)}
                />
              );
            })}
            {catalogue.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" ta="center" py="md">
                    No widgets registered
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}
